import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { EMAIL } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author @omerrkosar
 */

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const EmailAccountForm = NiceModal.create(({ id, mode, modalName, setEmailAccount }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					email: yup.string().email().nullable().required(),
					password: yup.string().nullable().required(),
					displayEmail: yup.string().nullable(),
					displayName: yup.string().nullable(),
					host: yup.string().nullable().required(),
					port: yup.string().nullable().required(),
					useTLS: yup.boolean().nullable().required(),
					useSSL: yup.boolean().nullable().required()
				})
				.required(),
		[]
	);

	const viewMode = mode === 'view';

	const { closeModal } = useModalRouter();
	const modal = useModal();

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const { showError, showSuccess } = useAlert();

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();

	const fillForm = useCallback(async () => {
		const defaultValues = {
			email: null,
			password: null,
			displayEmail: null,
			displayName: null,
			host: null,
			port: null,
			useTLS: true,
			useSSL: false
		};
		if (!id) {
			return defaultValues;
		}

		const response = await axiosPrivate.post(EMAIL.GET_EMAIL_ACCOUNT, {
			ID: id
		});

		const { data, status } = response;

		if (status !== 200) {
			showError();
			handleCloseModal();
			return;
		}

		return {
			...defaultValues,
			...data
		};
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		setValue,
		handleSubmit,
		watch,
		formState: { isSubmitting, isSubmitSuccessful, isLoading: isFormLoading, dirtyFields }
	} = formMethods;

	const isDirty = !!Object.keys(dirtyFields).length;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const onSubmit = async (data) => {
		const URL = id ? EMAIL.UPDATE_EMAIL_ACCOUNT : EMAIL.ADD_EMAIL_ACCOUNT;

		await axiosPrivate
			.post(URL, {
				ID: id,
				email: data.email,
				password: data.password,
				displayEmail: data.displayEmail,
				displayName: data.displayName,
				host: data.host,
				port: data.port,
				useTLS: data.useTLS,
				useSSL: data.useSSL
			})
			.then((response) => {
				const { status } = response;

				if (status !== 200) {
					showError();
					return;
				}

				showSuccess();
				if (setEmailAccount) setEmailAccount(response.data.ID);
				dispatch(updateDynamicListData(DYNAMICLISTS.EMAIL_ACCOUNTS));
				handleCloseModal();
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const defaultColSpan = {
		md: 6
	};

	return (
		<Modal
			fullwidth
			maxWidth="small"
			name={modalName}
			className="modal-emailAccounts"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={viewMode ? 'viewEmailAccount' : mode === 'edit' ? 'editEmailAccount' : 'addEmailAccounts'}
					tooltip={['edit', 'view'].includes(mode) ? watch('email') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{viewMode ? 'view' : mode === 'edit' ? 'editEmailAccount' : 'addEmailAccounts'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading} required>
						<StringInput name="email" disabled={viewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="password" isLoading={isLoading} required>
						<StringInput name="password" disabled={viewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="displayEmail" isLoading={isLoading}>
						<StringInput name="displayEmail" disabled={viewMode} placeholder={watch('email')} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="displayName" isLoading={isLoading}>
						<StringInput name="displayName" disabled={viewMode} />
					</FormItem>

					<FormItem
						colSpan={defaultColSpan}
						label="host"
						isLoading={isLoading}
						toolTip="hostDescription"
						required>
						<StringInput name="host" disabled={viewMode} />
					</FormItem>

					<FormItem
						colSpan={defaultColSpan}
						label="port"
						isLoading={isLoading}
						toolTip="portDescription"
						required>
						<IntegerInput name="port" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="useTLS" isLoading={isLoading} toolTip="useTLSDescription">
						<SwitchInput
							value={watch('useTLS')}
							setValue={(event) => {
								const { checked: value } = event.target;

								if (value) {
									setValue('useSSL', false, validateOptions);
								}

								setValue('useTLS', value, validateOptions);
							}}
							className="mt-3"
							disabled={viewMode}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="useSSL" isLoading={isLoading} toolTip="useSSLDescription">
						<SwitchInput
							value={watch('useSSL')}
							setValue={(event) => {
								const { checked: value } = event.target;

								if (value) {
									setValue('useTLS', false, validateOptions);
								}

								setValue('useSSL', value, validateOptions);
							}}
							className="mt-3"
							disabled={viewMode}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					<Button
						label={id ? 'save' : 'addNewAccount'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						iconPos="right"
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default EmailAccountForm;
